import { useState, useEffect, useCallback } from "react";
import {
  FiGrid, FiTag, FiList, FiShoppingCart, FiArchive, FiSliders,
  FiUsers, FiTruck, FiCreditCard, FiFileText, FiClipboard, FiSearch,
  FiClock, FiLogOut, FiLock, FiMapPin, FiRepeat, FiChevronDown,
  FiChevronLeft, FiChevronRight,
} from "react-icons/fi";
import { apiGet } from "./api";
import { getCurrentLocation, setCurrentLocation } from "./utils/location";
import Overview from "./components/Overview";
import Products from "./components/Products";
import Sales from "./components/Sales";
import Restock from "./components/Restock";
import Adjust from "./components/Adjust";
import Customers from "./components/Customers";
import Suppliers from "./components/Suppliers";
import Categories from "./components/Categories";
import Payments from "./components/Payments";
import Invoices from "./components/Invoices";
import RestockInvoices from "./components/RestockInvoices";
import Locations from "./components/Locations";
import Transfer from "./components/Transfer";

/* ── Nav definition ── */
const NAV = [
  { id: "overview",   label: "Overview",   mm: "အကျဉ်းချုပ်",   Icon: FiGrid,        accent: false },
  { id: "products",   label: "Products",   mm: "ကုန်ပစ္စည်း",   Icon: FiTag,         accent: false },
  { id: "categories", label: "Categories", mm: "အမျိုးအစား",   Icon: FiList,        accent: false },
  { id: "sale",       label: "New Sale",   mm: "အရောင်း",       Icon: FiShoppingCart, accent: true  },
  { id: "restock",    label: "Restock",    mm: "ပစ္စည်းဖြည့်",  Icon: FiArchive,     accent: false },
  { id: "transfer",   label: "Transfer",   mm: "ပြောင်းရွှေ့",   Icon: FiRepeat,      accent: false },
  { id: "adjust",     label: "Adjust",     mm: "ပြင်ဆင်",       Icon: FiSliders,     accent: false },
  { id: "customers",  label: "Customers",  mm: "ဖောက်သည်",     Icon: FiUsers,       accent: false },
  { id: "suppliers",  label: "Suppliers",  mm: "ပေးသွင်းသူ",    Icon: FiTruck,       accent: false },
  { id: "locations",  label: "Locations",  mm: "တည်နေရာ",      Icon: FiMapPin,      accent: false },
  { id: "payments",   label: "Payments",   mm: "ငွေပေးချေမှု",  Icon: FiCreditCard,  accent: false },
  { id: "invoices",   label: "Invoices",   mm: "ဘောက်ချာ",      Icon: FiFileText,    accent: false },
  { id: "restock-invoices", label: "Restock Invoices", mm: "ဝယ်ယူမှု ဘောက်ချာ", Icon: FiClipboard, accent: false },
];

const EMPLOYEE_ROUTES = new Set(["products", "sale", "payments"]);

function initials(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ── Sidebar ── */
function Sidebar({ route, go, collapsed, onToggle, isEmployee, userName, userRole }) {
  const W = collapsed ? 76 : 236;
  const navItems = isEmployee ? NAV.filter(n => EMPLOYEE_ROUTES.has(n.id)) : NAV;
  const init = initials(userName);

  return (
    <aside style={{
      width: W, flexShrink: 0,
      background: "var(--surface)",
      borderRight: "1px solid var(--line)",
      display: "flex", flexDirection: "column",
      transition: "width .2s ease",
      overflow: "hidden",
    }}>
      {/* Brand + collapse toggle */}
      <div style={{
        padding: collapsed ? "18px 0" : "18px 16px",
        display: "flex", alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        borderBottom: "1px solid var(--hair)", minHeight: 72,
        gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 13, flexShrink: 0,
            background: "var(--primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 15,
            color: "#fff", letterSpacing: "-.02em",
          }}>HKZ</div>
          {!collapsed && (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 800, letterSpacing: "-.02em", color: "var(--ink)", whiteSpace: "nowrap" }}>
                Hein Khant Zaw
              </div>
              <div style={{ fontSize: 11.5, color: "var(--sub)", marginTop: 1 }}>Clothing Wholesale</div>
            </div>
          )}
        </div>
        {!collapsed && (
          <button onClick={onToggle} style={toggleBtnStyle}>
            <FiChevronLeft size={16} color="var(--n-500)" />
          </button>
        )}
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
          <button onClick={onToggle} style={toggleBtnStyle}>
            <FiChevronRight size={16} color="var(--n-500)" />
          </button>
        </div>
      )}

      {/* Nav items */}
      <nav style={{ flex: 1, padding: collapsed ? "8px 10px" : "10px 10px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", overflowX: "hidden" }}>
        {navItems.map(n => {
          const active = route === n.id;
          return (
            <button key={n.id} onClick={() => go(n.id)} title={collapsed ? n.label : undefined}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: collapsed ? "10px 0" : "9px 11px",
                borderRadius: 10, border: "none", cursor: "pointer",
                fontFamily: "var(--font-body)", fontSize: 14,
                fontWeight: active ? 700 : 500, textAlign: "left", width: "100%",
                justifyContent: collapsed ? "center" : "flex-start",
                color: active
                  ? (n.accent ? "var(--accent)" : "var(--primary)")
                  : "var(--n-600)",
                background: active
                  ? (n.accent ? "var(--orange-50)" : "var(--blue-50)")
                  : "transparent",
                position: "relative",
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--faint)"; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? (n.accent ? "var(--orange-50)" : "var(--blue-50)") : "transparent"; }}
            >
              <n.Icon size={18} style={{ color: active ? (n.accent ? "var(--accent)" : "var(--primary)") : "var(--n-500)", flexShrink: 0 }} />
              {!collapsed && <span style={{ flex: 1 }}>{n.label}</span>}
              {n.accent && !active && (
                <span style={{
                  position: collapsed ? "absolute" : "static",
                  top: collapsed ? 8 : "auto", right: collapsed ? 14 : "auto",
                  width: 7, height: 7, borderRadius: 99, background: "var(--accent)",
                }} />
              )}
            </button>
          );
        })}
      </nav>

      {/* User footer */}
      <div style={{
        padding: collapsed ? "12px 0" : "11px 13px",
        borderTop: "1px solid var(--hair)",
        display: "flex", alignItems: "center", gap: 9,
        justifyContent: collapsed ? "center" : "flex-start",
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 99, flexShrink: 0,
          background: "var(--blue-50)", border: "1px solid var(--blue-100)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "var(--font-head)", fontSize: 12.5, fontWeight: 800,
          color: "var(--primary)",
        }}>{init}</div>
        {!collapsed && (
          <>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {userName || "User"}
              </div>
              <div style={{ fontSize: 11, color: "var(--success)", fontWeight: 600, marginTop: 1 }}>
                ● {userRole} · Clocked in
              </div>
            </div>
            <button title="Lock" onClick={() => go("__lock__")} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 5, flexShrink: 0 }}>
              <FiLock size={15} color="var(--n-400)" />
            </button>
          </>
        )}
      </div>
    </aside>
  );
}

const toggleBtnStyle = {
  width: 28, height: 28, borderRadius: 7,
  border: "1px solid var(--line)", background: "var(--surface)",
  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0,
};

/* ── Location switcher ── */
function LocationSwitcher({ locations, currentLocation, onChange }) {
  const [open, setOpen] = useState(false);
  if (locations.length === 0) return null;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(v => !v)} style={{ ...topbarBtnStyle, borderColor: "var(--blue-100)", background: "var(--blue-50)" }}>
        <FiMapPin size={16} color="var(--primary)" />
        <span style={{ fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 700, color: "var(--primary)" }}>
          {currentLocation?.name || "Choose location"}
        </span>
        <FiChevronDown size={14} color="var(--primary)" />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
          <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-md)", zIndex: 40, minWidth: 180, overflow: "hidden" }}>
            {locations.map(loc => (
              <button key={loc.id} onClick={() => { onChange(loc); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 14px", border: "none", background: currentLocation?.id === loc.id ? "var(--blue-50)" : "transparent", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: currentLocation?.id === loc.id ? 700 : 500, color: "var(--ink)", textAlign: "left" }}
                onMouseEnter={e => { if (currentLocation?.id !== loc.id) e.currentTarget.style.background = "var(--faint)"; }}
                onMouseLeave={e => { if (currentLocation?.id !== loc.id) e.currentTarget.style.background = "transparent"; }}
              >
                {loc.name}
                {loc.is_default && <span style={{ fontSize: 10.5, color: "var(--n-400)", fontWeight: 600 }}>DEFAULT</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Topbar ── */
function Topbar({ now, locations, currentLocation, onChangeLocation, onAttendance, onLogout }) {
  const h = now.getHours(), m = now.getMinutes();
  const h12 = h % 12 || 12;
  const timeStr = `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const ampm = h < 12 ? "AM" : "PM";
  const dateStr = now.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  return (
    <div style={{
      height: 60, flexShrink: 0,
      borderBottom: "1px solid var(--line)",
      background: "var(--surface)",
      display: "flex", alignItems: "center",
      justifyContent: "space-between",
      padding: "0 24px", gap: 16,
    }}>
      {/* Global search */}
      <div style={{
        display: "flex", alignItems: "center", gap: 9,
        background: "var(--n-50)", border: "1px solid var(--line)",
        borderRadius: "var(--r-sm)", padding: "9px 13px",
        width: 280,
      }}>
        <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
        <input
          placeholder="Search products, customers…"
          style={{
            border: "none", outline: "none", background: "transparent",
            fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%",
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {/* Current working location */}
        <LocationSwitcher locations={locations} currentLocation={currentLocation} onChange={onChangeLocation} />

        {/* Attendance button */}
        <button onClick={onAttendance} style={topbarBtnStyle}>
          <FiClock size={16} color="var(--n-500)" />
          <span style={{ fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>Attendance</span>
        </button>

        {/* Clock + date */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
            {timeStr} <span style={{ fontSize: 11, fontWeight: 600, color: "var(--n-400)" }}>{ampm}</span>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--sub)", marginTop: 2 }}>{dateStr}</div>
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 28, background: "var(--line)", flexShrink: 0 }} />

        {/* Log out */}
        <LogoutBtn onClick={onLogout} />
      </div>
    </div>
  );
}

const topbarBtnStyle = {
  display: "flex", alignItems: "center", gap: 7,
  border: "1px solid var(--line)", background: "var(--surface)",
  borderRadius: "var(--r-sm)", padding: "8px 12px", cursor: "pointer",
};

function LogoutBtn({ onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 7,
        border: `1px solid ${hover ? "transparent" : "var(--line)"}`,
        background: hover ? "var(--danger-bg)" : "var(--surface)",
        borderRadius: "var(--r-sm)", padding: "8px 12px", cursor: "pointer",
        transition: "background .12s, border-color .12s",
      }}>
      <FiLogOut size={16} color={hover ? "var(--danger)" : "var(--n-500)"} />
      <span style={{ fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 600, color: hover ? "var(--danger)" : "var(--ink)" }}>
        Log out
      </span>
    </button>
  );
}

/* ── Shell ── */
export default function InventoryDashboard({ token, role, inventoryUser, onDone, onAttendance }) {
  const isEmployee = !["OWNER", "MANAGER"].includes(role);
  const defaultRoute = isEmployee ? "products" : "overview";

  const [route, setRoute] = useState(defaultRoute);
  const [collapsed, setCollapsed] = useState(false);
  const [now, setNow] = useState(new Date());
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocationState] = useState(() => getCurrentLocation());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const r = await apiGet("/api/inventory/locations", token);
      const d = await r.json();
      const list = Array.isArray(d) ? d : [];
      setLocations(list);
      // First-ever load on this kiosk — default to the business's default
      // location (Home) so staff never see a blank switcher.
      setCurrentLocationState(prev => {
        if (prev && list.some(l => l.id === prev.id)) return prev;
        const fallback = list.find(l => l.is_default) || list[0] || null;
        if (fallback) setCurrentLocation(fallback);
        return fallback;
      });
    } catch { setLocations([]); }
  }, [token]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const changeLocation = (loc) => {
    setCurrentLocation(loc);
    setCurrentLocationState(loc);
  };

  const go = (screen) => {
    if (screen === "__lock__") { onDone(); return; }
    if (isEmployee && !EMPLOYEE_ROUTES.has(screen)) return;
    setRoute(screen);
  };

  const userName = inventoryUser?.name || "";
  const userRoleLabel = { OWNER: "Owner", MANAGER: "Manager", EMPLOYEE: "Cashier" }[role] || role;

  const screenProps = { token, role, go };
  const homeLocation = locations.find(l => l.is_default) || null;

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", fontFamily: "var(--font-body)" }}>
      <Topbar
        now={now}
        locations={locations}
        currentLocation={currentLocation}
        onChangeLocation={changeLocation}
        onAttendance={onAttendance || onDone}
        onLogout={onDone}
      />
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <Sidebar
          route={route}
          go={go}
          collapsed={collapsed}
          onToggle={() => setCollapsed(v => !v)}
          isEmployee={isEmployee}
          userName={userName}
          userRole={userRoleLabel}
        />
        <main style={{
          flex: 1, overflowY: "auto",
          background: "var(--bg)",
          padding: "24px 28px",
        }}>
          {route === "overview"   && <Overview   {...screenProps} />}
          {route === "products"   && <Products   token={token} role={role} go={go} />}
          {route === "categories" && <Categories token={token} role={role} go={go} />}
          {route === "sale"       && <Sales      token={token} role={role} go={go} location={currentLocation} />}
          {route === "restock"    && <Restock    token={token} role={role} go={go} homeLocation={homeLocation} />}
          {route === "transfer"   && <Transfer   token={token} role={role} go={go} locations={locations} currentLocation={currentLocation} />}
          {route === "adjust"     && <Adjust     token={token} role={role} go={go} location={currentLocation} />}
          {route === "customers"  && <Customers  token={token} role={role} go={go} />}
          {route === "suppliers"  && <Suppliers  token={token} role={role} go={go} />}
          {route === "locations"  && <Locations  token={token} role={role} go={go} onChanged={fetchLocations} />}
          {route === "payments"   && <Payments   token={token} role={role} go={go} />}
          {route === "invoices"   && <Invoices   token={token} role={role} go={go} />}
          {route === "restock-invoices" && <RestockInvoices token={token} role={role} go={go} />}
        </main>
      </div>
    </div>
  );
}
